import {
  Radio,
  RadioGroup,
  type TextInputSize,
} from "@/shared/foundation/primitives";

import { type BooleanComparator, isValueless } from "../FilterSchema";

interface BooleanValueInputProps {
  comparator: BooleanComparator;
  value: boolean[];
  onChange: (next: boolean[]) => void;
  /** Field size; a radio has no `lg`, so it reads as `md`. @default "md" */
  size?: TextInputSize;
}

/**
 * Value input for boolean-typed filters.
 *
 * Operator-aware:
 *   - IS_NULL / IS_NOT_NULL render no input; validity short-circuits to true.
 *   - EQUALS renders a True / False radio pair. The user must pick one to
 *     enable Add filter (no implicit default — boolean fields can be
 *     meaningfully nullable, and presupposing a default would silently
 *     filter for one truth value).
 *
 * Output is `[true]` or `[false]`. Multi-value boolean filters don't make
 * sense on a strictly two-state field, so this input is single-value-only.
 */
export function BooleanValueInput({
  comparator,
  value,
  onChange,
  size = "md",
}: BooleanValueInputProps) {
  const valueless = isValueless(comparator);

  if (valueless) {
    return null;
  }

  return (
    <RadioGroup<boolean>
      aria-label="Value"
      size={size === "sm" ? "sm" : "md"}
      value={value.length === 1 ? value[0] : null}
      onValueChange={(next) => {
        onChange([next]);
      }}
    >
      <Radio value label="True" />
      <Radio value={false} label="False" />
    </RadioGroup>
  );
}
